import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

const client = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Every page fires several parallel requests (e.g. a list + its filter dropdown data).
// If the access token has expired, all of them 401/403 around the same time — without a
// shared in-flight promise here, each one independently calls /auth/refresh, and a single
// transient failure among those parallel calls used to wipe localStorage and force a logout
// even when a sibling call's refresh had already succeeded. Sharing one promise means only
// one refresh happens per expiry, and every waiting request retries with its result.
let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) throw new Error('No refresh token');
  const res = await axios.post(`/api/auth/refresh`, { refreshToken });
  const data = (res.data as { data: { accessToken: string } }).data;
  localStorage.setItem('accessToken', data.accessToken);
  return data.accessToken;
}

client.interceptors.response.use(
  (response) => {
    const d = response.data as { success?: boolean; data?: unknown };
    return d?.data !== undefined ? d.data : response.data;
  },
  async (error: AxiosError<{ message?: string; code?: string }>) => {
    const status = error.response?.status;
    const config = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;

    // Public auth endpoints (login, register, forgot-password, etc.) are never called with an
    // existing session to refresh — a 401/403 from one of these is a real credentials/validation
    // error, not a session expiry, and must not trigger the refresh-then-redirect-to-login flow
    // (which would silently swallow the error and bounce the user right back to the login page).
    const isPublicAuthEndpoint = !!config?.url?.startsWith('/api/auth/') && !config.url.startsWith('/api/auth/me');

    // status is undefined on network/timeout errors — never treat those as a session expiry.
    if ((status === 401 || status === 403) && config && !isPublicAuthEndpoint && !config._retried) {
      config._retried = true;
      try {
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => { refreshPromise = null; });
        }
        const newToken = await refreshPromise;
        config.headers.Authorization = `Bearer ${newToken}`;
        return client.request(config);
      } catch {
        localStorage.clear();
        window.location.href = '/login';
        return new Promise(() => {}); // navigation is underway — never settle
      }
    }

    const msg  = error.response?.data?.message ?? error.message ?? 'An error occurred';
    const code = error.response?.data?.code;
    const err  = new Error(msg) as Error & { code?: string };
    err.code   = code;
    return Promise.reject(err);
  },
);

export default client;
